import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useModal } from "@/components/essex/ModalContext";
import FeaturedFilms from "@/components/essex/FeaturedFilms";
import StatsSection from "@/components/essex/StatsSection";
import {
  Video,
  Camera,
  Plane,
  MessageSquareHeart,
  Check,
  Star,
  ArrowRight,
  ChevronDown,
  Users,
  Award,
  Clock,
  Shield,
} from "lucide-react";

const CONTACT_URL = "https://www.weddingvideo.co.uk/contact";

const HERO_VIDEO = "https://customer-assets.emergentagent.com/job_wve-wedding-showcase/artifacts/b9pkkc6t_HERO%20VIDEO.mp4";
const BOOTH_IMAGE = "https://static.wixstatic.com/media/1fe187_388133fe6fb049eea635ff4eba8dc239~mv2.jpg/v1/fill/w_608,h_812,al_c,q_85,enc_avif,quality_auto/1fe187_388133fe6fb049eea635ff4eba8dc239~mv2.jpg";
const BOOTH_ACTION = "https://static.wixstatic.com/media/1fe187_2fcf07f7c2a54845aa4d9e60be93ab33~mv2.png/v1/fit/w_960,h_721,q_90,enc_avif,quality_auto/1fe187_2fcf07f7c2a54845aa4d9e60be93ab33~mv2.png";

function CTAButton({ text = "Check My Date", large = false, testId = "cta-btn" }) {
  return (
    <a
      href={CONTACT_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={`group inline-flex items-center justify-center gap-2 bg-[#C2A878] text-[#0A0908] font-sans font-semibold tracking-wide uppercase hover:bg-[#d4bc92] transition-all duration-300 ${
        large ? "px-12 py-5 text-sm" : "px-8 py-4 text-xs"
      }`}
    >
      {text}
      <ArrowRight size={large ? 16 : 14} className="group-hover:translate-x-1 transition-transform" />
    </a>
  );
}

function HeroSection() {
  return (
    <section
      data-testid="lp-hero"
      className="relative min-h-[100vh] flex items-center justify-center bg-[#0A0908] overflow-hidden"
    >
      {/* Background video with overlay */}
      <div className="absolute inset-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <p className="text-xs tracking-[0.3em] uppercase text-[#C2A878] font-sans mb-8">
            Wedding Video Essex
          </p>

          <h1
            data-testid="lp-hero-heading"
            className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight font-light text-white leading-[1.1]"
          >
            The Most Important Guests<br />
            You'll Invite.
          </h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="mt-10 max-w-2xl mx-auto"
          >
            <p className="text-base md:text-lg text-[#F5F5F0]/90 font-sans leading-relaxed">
              You're welcoming a hundred of your closest family and friends.
              Invite three more. We're the ones who make sure none of it slips away.
            </p>
            <p className="mt-4 text-sm text-[#A19D94]/70 font-sans leading-relaxed">
              Award-winning wedding videographers and photographers in Essex.
              Full day coverage with every moment caught on camera. 900+ weddings captured over the last 12 years.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="mt-10"
          >
            <CTAButton text="Check My Date" large testId="lp-hero-cta" />
            <p className="mt-3 text-xs text-[#A19D94]/50 font-sans">
              No obligation. We reply within 24 hours.
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <ChevronDown size={20} className="text-[#C2A878]/40 animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
}

function TrustBar() {
  const stats = [
    { number: "900+", label: "Couples filmed" },
    { number: "7x", label: "Award winners" },
    { number: "5.0", label: "Average rating" },
    { number: "21", label: "Venue partners" },
  ];

  return (
    <section data-testid="lp-trust-bar" className="bg-[#0D0C0B] border-y border-white/[0.04] py-8">
      <div className="max-w-5xl mx-auto px-6 flex flex-wrap justify-center gap-x-12 gap-y-4">
        {stats.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.4 }}
            className="text-center"
          >
            <span className="text-2xl font-serif text-[#C2A878]">{s.number}</span>
            <span className="block text-xs text-[#A19D94]/60 font-sans mt-1">{s.label}</span>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function WhatYouGetSection() {
  const items = [
    {
      icon: Video,
      title: "Cinematic Wedding Film",
      points: [
        "Two videographers, full day",
        "20 to 30 minute cinematic film",
        "3 to 4 minute highlight in 72 hours",
        "Full ceremony and speeches edits",
        "Canon Cinema Cameras and prime lenses",
      ],
    },
    {
      icon: Camera,
      title: "Documentary Photography",
      points: [
        "One dedicated photographer, full day",
        "Natural, unposed documentary style",
        "500+ edited photos in online gallery",
        "30 to 40 sneak peeks within one week",
        "Designed to match your film",
      ],
    },
    {
      icon: Plane,
      title: "Your Venue From the Sky",
      points: [
        "Sweeping aerial shots of your venue and grounds",
        "The two of you walking hand in hand, seen from above",
        "Woven naturally into your film",
        "Weather and venue permitting",
      ],
    },
    {
      icon: MessageSquareHeart,
      title: "Video Message Booth",
      subtitle: "Complimentary",
      points: [
        "Slimline booth at your venue all day",
        "Guests tap record and leave messages",
        "Edited into a separate keepsake film",
        "Replaces paper guestbooks and photo booths",
      ],
    },
  ];

  return (
    <section data-testid="lp-whats-included" className="bg-[#0A0908] py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans mb-4">
            Everything in one package
          </p>
          <h2
            data-testid="lp-whats-included-heading"
            className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light text-[#F5F5F0]"
          >
            Here's what you get.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {items.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`lp-included-${i}`}
              className="bg-[#11100F] border border-white/[0.06] p-8 md:p-10"
            >
              <div className="flex items-center gap-3 mb-5">
                <item.icon className="w-5 h-5 text-[#C2A878]" strokeWidth={1.5} />
                <h3 className="font-serif text-xl text-[#F5F5F0] tracking-tight">{item.title}</h3>
                {item.subtitle && (
                  <span className="text-[10px] tracking-wider uppercase bg-[#C2A878]/15 text-[#C2A878] px-2 py-0.5 font-sans">
                    {item.subtitle}
                  </span>
                )}
              </div>
              <ul className="space-y-2.5">
                {item.points.map((p, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-sm text-[#A19D94]/80 font-sans">
                    <Check size={14} className="mt-0.5 text-[#C2A878] flex-shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ZeroPosing() {
  return (
    <section data-testid="lp-zero-posing" className="bg-[#0D0C0B] py-20 md:py-24 border-y border-white/[0.04]">
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light text-[#F5F5F0]">
            Zero awkward posing.
          </h2>
          <p className="mt-4 text-sm md:text-base text-[#A19D94] font-sans leading-relaxed">
            Hate being in front of the camera? We hear you. So do all our couples.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

function VideoBoothSection() {
  return (
    <section data-testid="lp-video-booth" className="bg-[#FAF9F7] py-24 md:py-32">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center"
        >
          <div>
            <div className="flex items-center gap-3 mb-6">
              <MessageSquareHeart className="w-6 h-6 text-[#9A7D50]" strokeWidth={1.5} />
              <span className="text-[10px] tracking-wider uppercase bg-[#C2A878]/15 text-[#9A7D50] px-2.5 py-1 font-sans font-medium">
                Included free with every package
              </span>
            </div>

            <h2
              data-testid="lp-booth-heading"
              className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light text-[#1A1917] leading-tight"
            >
              Your guests. On camera.<br />
              Saying things they'd never write in a card.
            </h2>

            <div className="mt-8 space-y-5 text-[#5A5750] font-sans text-sm md:text-base leading-relaxed">
              <p>
                Forget paper guestbooks nobody reads and the photo booth that ate £600 of your budget.
                Our slimline video booth tucks into a corner of your venue all day. Your guests tap record,
                and you end up with a film of every drunk uncle, sobbing best mate, and three-year-old
                saying something completely cursed.
              </p>
              <p className="text-[#1A1917] font-medium">
                It's like a confession booth. Most-watched thing on your hard drive in 10 years, guaranteed.
              </p>
            </div>

            <div className="mt-8">
              <a
                href="https://www.weddingvideo.co.uk/investment/video-guestbook"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lp-booth-link"
                className="inline-flex items-center gap-2 text-sm font-sans text-[#9A7D50] hover:text-[#7A6440] transition-colors group"
              >
                See the Video Message Booth in action
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>

          <div className="relative">
            <img
              src={BOOTH_IMAGE}
              alt="Wedding Video Essex Video Message Booth at a wedding venue"
              className="w-full max-w-sm mx-auto rounded-sm shadow-lg"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function WhyOneTeamSection() {
  const reasons = [
    { icon: Users, text: "Two videographers and one photographer. Every wedding." },
    { icon: Award, text: "7x award-winning. 900+ Essex couples." },
    { icon: Clock, text: "Sneak peek film in 72 hours. Photos in one week." },
    { icon: Shield, text: "One team, one booking. No coordination headaches." },
    { icon: Camera, text: "Natural, documentary style photography all day." },
    { icon: Video, text: "Canon Cinema Cameras. Drone footage. Cinematic editing." },
  ];

  return (
    <section data-testid="lp-why-one-team" className="bg-[#0A0908] py-24 md:py-32">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans mb-4">
            Why one team matters
          </p>
          <h2
            data-testid="lp-why-heading"
            className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light text-[#F5F5F0] leading-tight"
          >
            One team. One style.<br className="hidden sm:block" /> No coordination headaches.
          </h2>
          <p className="mt-4 text-sm text-[#A19D94] font-sans leading-relaxed max-w-2xl">
            Our photographers and videographers work side by side every weekend. They know exactly where
            each other will be. No missed moments. One consistent style across your photos and film.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {reasons.map((r, i) => (
            <motion.div
              key={r.text}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="flex items-start gap-3 p-4 bg-white/[0.02] border border-white/[0.04]"
            >
              <r.icon size={16} className="mt-0.5 text-[#C2A878] flex-shrink-0" strokeWidth={1.5} />
              <span className="text-sm text-[#A19D94]/90 font-sans">{r.text}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  const testimonials = [
    {
      quote: "I've watched our wedding film over 30 times and I cry every single time. Worth every single penny.",
      couple: "Jessica & David",
      venue: "Gaynes Park",
    },
    {
      quote: "The best decision we made was booking both photo and video as one team. The photos are gorgeous and the film captured moments we didn't even know happened.",
      couple: "Charlotte & James",
      venue: "Gosfield Hall",
    },
    {
      quote: "My mum called me sobbing after watching the film. Even my husband, who said he'd never watch it, has seen it about 20 times.",
      couple: "Sophie & Alex",
      venue: "Leez Priory",
    },
  ];

  return (
    <section data-testid="lp-testimonials" className="bg-[#FAF9F7] py-24 md:py-32">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans mb-4">
            Don't take our word for it
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light text-[#1A1917]">
            Real couples. Real words.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <motion.div
              key={t.couple}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`lp-testimonial-${i}`}
              className="bg-white p-8 border border-[#E8E4DD]"
            >
              <div className="flex gap-0.5 mb-4">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} size={12} className="text-[#C2A878] fill-[#C2A878]" />
                ))}
              </div>
              <p className="text-sm text-[#3A3835] font-sans leading-relaxed italic">
                "{t.quote}"
              </p>
              <div className="mt-5 pt-4 border-t border-[#E8E4DD]">
                <p className="text-sm font-sans font-medium text-[#1A1917]">{t.couple}</p>
                <p className="text-xs text-[#A19D94] font-sans">{t.venue}, Essex</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="text-center mt-10"
        >
          <a
            href="https://weddingvideoessexreviews.co.uk/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="lp-reviews-link"
            className="inline-flex items-center gap-2 text-sm font-sans text-[#9A7D50] hover:text-[#7A6440] transition-colors group"
          >
            Read over 400 more reviews
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}

function PricingSection() {
  return (
    <section data-testid="lp-pricing" className="bg-[#0A0908] py-24 md:py-32">
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans mb-4">
            Investment
          </p>
          <h2
            data-testid="lp-pricing-heading"
            className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light text-[#F5F5F0] mb-6"
          >
            The Complete Package
          </h2>

          <div className="inline-block bg-[#11100F] border border-white/[0.06] p-10 md:p-14 text-left max-w-lg mx-auto w-full">
            <p className="text-sm text-[#C2A878] font-sans tracking-wider uppercase mb-2">
              Video + Photo + Drone + Booth
            </p>
            <div className="flex items-baseline gap-2 mb-6">
              <span className="text-sm text-[#A19D94]/60 font-sans uppercase tracking-wide">From</span>
              <span className="font-serif text-4xl md:text-5xl text-[#F5F5F0]">£3,000</span>
              <span className="text-sm text-[#A19D94] font-sans">or from £250/mo</span>
            </div>

            <ul className="space-y-2.5 mb-8">
              {[
                "Two videographers and one photographer",
                "Full day coverage, bridal prep to first dance",
                "20 to 30 minute cinematic wedding film",
                "Highlight film delivered within 72 hours",
                "Full ceremony and speeches edits",
                "Drone footage",
                "500+ edited photos in online gallery",
                "Complimentary Video Message Booth",
                "Private online delivery",
                "No hidden charges",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-[#A19D94]/80 font-sans">
                  <Check size={14} className="mt-0.5 text-[#C2A878] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <CTAButton text="Check My Date" testId="lp-pricing-cta" />

            <p className="mt-4 text-xs text-[#A19D94]/40 font-sans">
              Interest free monthly payments. No deposit required to enquire.
            </p>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-8 text-sm text-[#A19D94]/60 font-sans"
          >
            Video-only and photo-only packages also available from £1,500.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

function UrgencySection() {
  return (
    <section data-testid="lp-urgency" className="bg-[#0D0C0B] py-24 md:py-32">
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light text-[#F5F5F0] leading-tight">
            We film 100+ weddings a year.<br />
            Peak weekends book out months ahead.
          </h2>
          <p className="mt-6 text-sm text-[#A19D94] font-sans leading-relaxed max-w-xl mx-auto">
            Pop your date in and we'll check availability. No commitment.
            No sales pitch. Just a quick reply letting you know if we're free.
          </p>
          <div className="mt-8">
            <CTAButton text="Check If Your Date Is Free" large testId="lp-final-cta" />
          </div>
          <p className="mt-3 text-xs text-[#A19D94]/40 font-sans">
            We reply within 24 hours. Usually faster.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

function MinimalFooter() {
  return (
    <footer data-testid="lp-footer" className="bg-[#0A0908] border-t border-white/[0.04] py-10">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <p className="font-serif text-lg text-[#F5F5F0]/80 tracking-tight">
          Wedding Video <span className="text-[#C2A878]">Essex</span>
        </p>
        <p className="text-xs text-[#A19D94]/40 font-sans mt-2">
          Award-winning wedding videography and photography. Wickford, Essex.
        </p>
        <div className="flex justify-center gap-6 mt-4">
          <a href="https://www.instagram.com/weddingvideoessex" target="_blank" rel="noopener noreferrer" className="text-xs text-[#A19D94]/50 hover:text-[#C2A878] font-sans transition-colors">
            Instagram
          </a>
          <a href="https://www.youtube.com/@WeddingVideoEssex" target="_blank" rel="noopener noreferrer" className="text-xs text-[#A19D94]/50 hover:text-[#C2A878] font-sans transition-colors">
            YouTube
          </a>
          <a href="https://essex.weddingvideo.co.uk" className="text-xs text-[#A19D94]/50 hover:text-[#C2A878] font-sans transition-colors">
            Main Site
          </a>
        </div>
      </div>
    </footer>
  );
}

export default function VideoPhotoLanding() {
  return (
    <div data-testid="video-photo-landing" className="bg-[#0A0908] min-h-screen">
      <Helmet>
        <title>Wedding Video & Photography Essex | Complete Package from £3,000 | Wedding Video Essex</title>
        <meta
          name="description"
          content="Video. Photography. Drone footage. And a complimentary Video Message Booth. All delivered by one award-winning team. Already chosen by 900+ Essex couples. Complete packages from £3,000."
        />
        <link rel="canonical" href="https://essex.weddingvideo.co.uk/video-photo" />
      </Helmet>

      <HeroSection />
      <StatsSection />
      <WhatYouGetSection />
      <ZeroPosing />
      <FeaturedFilms />
      <VideoBoothSection />
      <WhyOneTeamSection />
      <TestimonialsSection />
      <PricingSection />
      <UrgencySection />
      <MinimalFooter />
    </div>
  );
}
